import { StyledButton } from '../../styles';
import { Text } from '@/components/atoms/text';
import { useTheme } from '@mui/material/styles';
import { Skeleton, useMediaQuery } from '@mui/material';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { MunicipalityComparative, MunicipalitiesComparative } from '@/graphql/types/municipality';
import { buildChartDataByMunicipality, buildSeriesList } from '@/utils/functions/chart';
import { useTranslation } from '@/hooks/useTranslation';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { StyledContainer, StyledFlex, StyledPaper } from './styles';
import { MunicipalityActions } from '@/components/molecules/municipalityActions';
import { useSeriesColors } from '@/hooks/useSeriesColors';
import { Charts } from './charts';
import { CompareArrows, FileDownloadOutlined } from '@mui/icons-material';
import { useMemo } from 'react';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { MenuButton, MenuButtonOption } from '@/components/molecules/button/menu';
import { DownloadUsage } from '@/graphql/types/downloads';
import { Flex } from '@/styles/globalStyles';
import { OpportunityAndRiskCard } from '@/components/molecules/cards/opportunityAndRisk';
import { GenericChart } from '../generic/chart';
import type { ClassificationOpportunityAndRisk } from '@/types/classification';
import { ComparativeMunicipalitiesMap } from '@/components/molecules/comparativeMunicipalitiesMap';

interface GeneralTabProps {
  comparativeData: {
    data: MunicipalitiesComparative[];
    onInsight: () => void;
    onCompare: () => void;
  };
  downloadOptions?: MenuButtonOption[];
  names: {
    municipality: string;
    state: string;
  };
  loadingData?: boolean;
  loadingPDF?: boolean;
  loadingXLSX?: boolean;
  downloadUsage?: DownloadUsage;
  limitReachedMessage?: string;
}

export const GeneralTab = ({
  loadingData,
  names,
  comparativeData,
  downloadOptions,
  loadingPDF,
  loadingXLSX,
  downloadUsage,
  limitReachedMessage,
}: GeneralTabProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();

  const transformedData: MunicipalityComparative[] = useMemo(() => {
    return comparativeData.data.flatMap((municipality) => {
      return Object.entries(municipality.dimensions).map(([, dimensionData]) => ({
        dimension: {
          ...dimensionData.dimension,
          tooltip: dimensionData.dimension.tooltip || '',
        },
        municipality_opportunity: parseFloat(dimensionData.municipality_opportunity || '0'),
        municipality_risk: parseFloat(dimensionData.municipality_risk || '0'),
        state_average_opportunity: parseFloat(dimensionData.state_average_opportunity || '0'),
        state_average_risk: parseFloat(dimensionData.state_average_risk || '0'),
        classification_opportunity: (dimensionData.classification_opportunity ||
          municipality.classification.opportunity) as any,
        classification_risk: (dimensionData.classification_risk ||
          municipality.classification.risk) as any,
        municipality: {
          name_municipality: municipality.municipality.name_municipality,
          municipality_code: municipality.municipality.municipality_code,
          name_uf: municipality.municipality.name_uf,
          display_name: municipality.municipality.name_municipality,
        },
        intensity_color_opportunity: dimensionData.intensity_color_opportunity || '#116938',
        intensity_color_risk: dimensionData.intensity_color_risk || '#fe9b33',
      }));
    });
  }, [comparativeData.data]);

  const seriesColors = useSeriesColors(transformedData);

  const opportunityComparativeChartData = useMemo(() => {
    return buildChartDataByMunicipality(
      transformedData.filter((item) => item.dimension.bigquery_id !== 'seguranca'),
      'municipality_opportunity'
    ).slice(0, 6);
  }, [transformedData]);

  const riskComparativeChartData = useMemo(() => {
    return buildChartDataByMunicipality(
      transformedData.filter((item) => item.dimension.bigquery_id !== 'economica'),
      'municipality_risk'
    ).slice(0, 6);
  }, [transformedData]);

  const seriesComparative = useMemo(() => {
    return buildSeriesList(transformedData, seriesColors).slice(0, 6);
  }, [transformedData, seriesColors]);

  const comparativeOpportunityData = comparativeData?.data.map((item) => {
    const dimensionData = Object.values(item.dimensions)[0];
    // Fallback to a default value if classification is undefined
    const classification =
      (dimensionData?.classification_opportunity as ClassificationOpportunityAndRisk) ||
      (item.classification.opportunity as ClassificationOpportunityAndRisk) ||
      'Baixa'; // Replace 'Baixa' with a valid default value from your enum/type

    return {
      title: item.municipality.name_municipality,
      classification,
      data: {
        note: dimensionData?.municipality_opportunity ?? item.score.opportunity ?? 0, // fallback to 0 if undefined
        color: dimensionData?.intensity_color_opportunity || '#116938',
      },
    };
  });

  const comparativeRiskData = comparativeData?.data.map((item) => {
    const dimensionData = Object.values(item.dimensions)[0];
    const classification =
      (dimensionData?.classification_risk as ClassificationOpportunityAndRisk) ||
      (item.classification.risk as ClassificationOpportunityAndRisk) ||
      'Baixa'; // Replace 'Baixa' with a valid default value from your enum/type

    return {
      title: item.municipality.name_municipality,
      classification,
      data: {
        note: dimensionData?.municipality_risk ?? item.score.risk ?? 0, // fallback to 0 if undefined
        color: dimensionData?.intensity_color_risk || '#fe9b33',
      },
    };
  });

  const ComparativeButtons = ({ fullWidth }: { fullWidth?: boolean }) => {
    return (
      <StyledContainer>
        <StyledButton
          variant="outlined"
          startIcon={<SparklesIcon color="inherit" />}
          onClick={() => comparativeData.onInsight()}
          fullWidth={fullWidth}
        >
          <SafeMarkdown text={t('button.button.insights')} />
        </StyledButton>

        {downloadOptions && downloadOptions.length > 0 && (
          <MenuButton
            options={downloadOptions}
            icon={FileDownloadOutlined}
            label={t('button.button.export')}
            loading={loadingPDF || loadingXLSX}
            variant="outlined"
            downloadUsage={downloadUsage}
            limitReachedMessage={limitReachedMessage}
          />
        )}

        <StyledButton
          variant="contained"
          startIcon={<CompareArrows color="inherit" />}
          onClick={() => comparativeData.onCompare()}
          fullWidth={fullWidth}
        >
          <SafeMarkdown text={t('button.button.compare_municipality')} />
        </StyledButton>
      </StyledContainer>
    );
  };

  return (
    <>
      {loadingData ? (
        <Skeleton animation="wave" height={554} width="100%" sx={{ transform: 'none' }} />
      ) : (
        <>
          <StyledPaper variant="outlined">
            {comparativeData.data.length > 0 ? (
              <>
                <StyledFlex
                  justify="space-between"
                  align={isMobile ? 'flex-start' : 'center'}
                  gap={isMobile ? 2 : 0}
                  fullWidth
                >
                  <Text variant={isMobile ? 'subtitle1' : 'h6'} color="text.primary">
                    <SafeMarkdown text={t('modal.modal.municipality.compare.title')} />
                  </Text>

                  <Desktop>
                    <ComparativeButtons />
                  </Desktop>
                </StyledFlex>

                {/* Comparative Bar Charts */}
                <Flex gap={3} justify="space-between" align="stretch" fullWidth>
                  <OpportunityAndRiskCard variant="opportunity">
                    <GenericChart chartData={comparativeOpportunityData} variant="opportunity" />
                  </OpportunityAndRiskCard>

                  <OpportunityAndRiskCard variant="risk">
                    <GenericChart chartData={comparativeRiskData} variant="risk" />
                  </OpportunityAndRiskCard>
                </Flex>

                {/* Comparative Radar Charts */}
                <Charts
                  municipalityName={names.municipality}
                  series={seriesComparative}
                  opportunityChartData={opportunityComparativeChartData}
                  riskChartData={riskComparativeChartData}
                />

                <Flex
                  direction="column"
                  align="stretch"
                  fullWidth
                  sx={{ marginTop: theme.spacing(3) }}
                >
                  <ComparativeMunicipalitiesMap data={comparativeData.data} dimensionId={0} />
                </Flex>

                <Mobile>
                  <ComparativeButtons fullWidth />
                </Mobile>
              </>
            ) : (
              <MunicipalityActions
                hasComparison={comparativeData.data.length > 0}
                compareButton={{
                  onClick: () => comparativeData.onCompare(),
                }}
                insightButton={{
                  onClick: () => comparativeData.onInsight(),
                }}
                downloadOptions={downloadOptions}
                loadingPDF={loadingPDF}
                loadingXLSX={loadingXLSX}
                downloadUsage={downloadUsage}
                limitReachedMessage={limitReachedMessage}
              />
            )}
          </StyledPaper>
        </>
      )}
    </>
  );
};
